import { useEffect, useState } from "react";
import { toIsoZ } from "./format.js";

const IMAGE_CACHE_NAME = "image-pro:generated-images:v1";
const IMAGE_OBJECT_URL_CACHE = new Map();
const IMAGE_OBJECT_URL_PROMISES = new Map();
const IMAGE_LOAD_PROMISES = new Map();

export function getDisplayImageSrc(item) {
  const primary = getImageSetItems(item)[0] || {};
  return primary.displayUrl || primary.dataUrl || primary.imageUrl || primary.cachedImageUrl || item?.displayUrl || item?.dataUrl || item?.imageUrl || item?.cachedImageUrl || "";
}

export function getDownloadImageSrc(item) {
  const primary = getImageSetItems(item)[0] || {};
  return primary.cachedImageUrl || item?.cachedImageUrl || mainSystemAssetPath(primary.displayUrl || primary.imageUrl || item?.displayUrl || item?.imageUrl) || getDisplayImageSrc(item);
}

export function getPreviewPlaceholderSrc(item) {
  const primary = getImageSetItems(item)[0] || {};
  return item?.thumbnailUrl || item?.thumbnail_url || primary.thumbnailUrl || primary.thumbnail_url || thumbnailUrlForDisplay(primary.cachedImageUrl || item?.cachedImageUrl || "") || "";
}

export function getThumbnailImageSrc(item) {
  const primary = getImageSetItems(item)[0] || {};
  return item?.thumbnailUrl
    || item?.thumbnail_url
    || primary.thumbnailUrl
    || primary.thumbnail_url
    || thumbnailUrlForDisplay(primary.cachedImageUrl || primary.displayUrl || primary.imageUrl || item?.cachedImageUrl || item?.displayUrl || item?.imageUrl)
    || getDisplayImageSrc(item);
}

export function getImageSetItems(item) {
  const images = Array.isArray(item?.images)
    ? item.images.map(normalizeImageAssetItem).filter(Boolean)
    : [];
  if (images.length > 0) {
    return images;
  }
  const primary = normalizeImageAssetItem(item);
  return primary ? [primary] : [];
}

export function getGenerationTextResult(item) {
  const resultType = String(item?.type || item?.resultType || item?.result_type || "").trim();
  if (resultType !== "text_rejection") {
    return "";
  }
  return String(item?.rejectionText || item?.rejection_text || item?.text || item?.message || "").trim();
}

export function isGenerationTextResult(item) {
  return Boolean(getGenerationTextResult(item));
}

export function thumbnailUrlForDisplay(src) {
  try {
    const url = new URL(src || "", window.location.href);
    const prefix = "/generated/";
    if (url.origin === window.location.origin && url.pathname.startsWith(prefix)) {
      return `/generated-thumbnails/${url.pathname.slice(prefix.length)}`;
    }
  } catch {
    return "";
  }
  return "";
}

export function buildDownloadName(item, src) {
  const model = sanitizeFilePart(item?.model || "generated-image");
  const timestamp = formatFileTimestamp(item?.createdAt || Date.now());
  const taskId = sanitizeFilePart(item?.id || "").slice(0, 12);
  const suffix = taskId ? `-${taskId}` : "";
  return `${model}-${timestamp}${suffix}.${detectImageExtension(src || item?.imageUrl)}`;
}

export async function handleImageDownload(event, item, src) {
  const url = downloadableSameOriginUrl(src);
  if (!url || typeof fetch !== "function") {
    return;
  }
  event.preventDefault();
  let objectUrl = "";
  try {
    const response = await fetch(url.href, { credentials: "same-origin" });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    const blob = await response.blob();
    objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = buildDownloadName(item, src);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } catch (error) {
    console.warn("Failed to download image", error);
    if (!isExternalGeneratedImageUrl(src)) {
      window.location.href = src;
    }
  } finally {
    if (objectUrl) {
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
  }
}

export function normalizeHistoryItems(items) {
  return Array.isArray(items)
    ? items.map(normalizeHistoryItem).filter((item) => item.id && (getDisplayImageSrc(item) || isGenerationTextResult(item)))
    : [];
}

export function expandHistoryItems(items) {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.flatMap((item) => expandHistoryItem(item));
}

function expandHistoryItem(item) {
  if (isGenerationTextResult(item)) {
    return [item];
  }
  const imageItems = getImageSetItems(item);
  if (imageItems.length <= 1) {
    return [item];
  }
  const baseId = String(item?.id || item?.taskId || item?.task_id || "").trim();
  const taskId = String(item?.taskId || item?.task_id || baseId).trim();
  return imageItems.map((image, index) => ({
    ...item,
    id: `${baseId || taskId}-${index + 1}`,
    sourceId: baseId,
    source_id: baseId,
    taskId,
    task_id: taskId,
    imageUrl: image.imageUrl || image.image_url || image.displayUrl || image.display_url || "",
    image_url: image.image_url || image.imageUrl || image.display_url || image.displayUrl || "",
    displayUrl: image.displayUrl || image.display_url || image.imageUrl || image.image_url || "",
    display_url: image.display_url || image.displayUrl || image.image_url || image.imageUrl || "",
    cachedImageUrl: image.cachedImageUrl || image.cached_image_url || "",
    cached_image_url: image.cached_image_url || image.cachedImageUrl || "",
    thumbnailUrl: image.thumbnailUrl || image.thumbnail_url || "",
    thumbnail_url: image.thumbnail_url || image.thumbnailUrl || "",
    images: [image],
    imageIndex: index,
    image_index: index,
    imageSetCount: imageItems.length,
    image_set_count: imageItems.length,
  }));
}

export function normalizeHistoryItem(item) {
  const source = item && typeof item === "object" ? item : {};
  const taskId = String(source.taskId || source.task_id || source.id || "").trim();
  const id = String(source.id || taskId).trim();
  const images = Array.isArray(source.images) ? source.images.map(normalizeImageAssetItem).filter(Boolean) : [];
  const primary = images[0] || {};
  const displayUrl = source.displayUrl || source.display_url || source.url || source.imageUrl || source.image_url || primary.displayUrl || primary.display_url || "";
  const cachedImageUrl = source.cachedImageUrl || source.cached_image_url || primary.cachedImageUrl || primary.cached_image_url || "";
  const resultType = String(source.type || source.resultType || source.result_type || "").trim();
  const rejectionText = String(source.rejectionText || source.rejection_text || source.text || source.message || "").trim();
  const normalizedImages = images.length > 0
    ? images
    : getImageSetItems({ ...source, displayUrl, cachedImageUrl });
  return {
    id,
    taskId,
    type: resultType,
    resultType,
    result_type: resultType,
    rejectionText,
    rejection_text: rejectionText,
    text: rejectionText,
    message: rejectionText,
    finishReason: source.finishReason || source.finish_reason || "",
    finish_reason: source.finish_reason || source.finishReason || "",
    imageUrl: source.imageUrl || source.image_url || source.url || displayUrl,
    displayUrl,
    cachedImageUrl,
    thumbnailUrl: source.thumbnailUrl || source.thumbnail_url || thumbnailUrlForDisplay(cachedImageUrl || displayUrl),
    images: normalizedImages,
    prompt: source.prompt || "",
    model: source.model || "",
    aspectRatio: source.aspectRatio || source.aspect_ratio || "",
    createdAt: source.createdAt || source.created_at || Date.now(),
  };
}

export function useCachedImageSrc(src, enabled = true) {
  const [cachedSrc, setCachedSrc] = useState(() => (enabled ? getMemoryImageObjectUrl(src) : "") || src || "");
  useEffect(() => {
    let disposed = false;
    if (!src) {
      setCachedSrc("");
      return undefined;
    }
    if (!enabled) {
      setCachedSrc(src);
      return undefined;
    }
    const memoryUrl = getMemoryImageObjectUrl(src);
    if (memoryUrl) {
      setCachedSrc(memoryUrl);
      return () => {
        disposed = true;
      };
    }
    setCachedSrc(src);
    getCachedImageObjectUrl(src)
      .then((objectUrl) => {
        if (disposed || !objectUrl) {
          return;
        }
        setCachedSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      disposed = true;
    };
  }, [enabled, src]);
  return cachedSrc;
}

export function useProgressiveImageSrc(src, placeholderSrc = "") {
  const [displaySrc, setDisplaySrc] = useState(() => selectProgressiveInitialSrc(src, placeholderSrc));
  useEffect(() => {
    let disposed = false;
    if (!src) {
      setDisplaySrc("");
      return undefined;
    }
    const highResMemoryUrl = getMemoryImageObjectUrl(src);
    if (highResMemoryUrl) {
      setDisplaySrc(highResMemoryUrl);
      return () => {
        disposed = true;
      };
    }
    setDisplaySrc(selectProgressiveInitialSrc(src, placeholderSrc));
    getCachedImageObjectUrl(src)
      .catch(() => "")
      .then((objectUrl) => {
        if (disposed) {
          return "";
        }
        if (objectUrl) {
          setDisplaySrc(objectUrl);
        }
        return preloadImage(src);
      })
      .then((loadedSrc) => {
        if (!disposed && loadedSrc) {
          setDisplaySrc(loadedSrc);
        }
      });
    return () => {
      disposed = true;
    };
  }, [placeholderSrc, src]);
  return displaySrc;
}

export async function purgeCachedHistoryItem(item) {
  const imageItems = getImageSetItems(item);
  await Promise.all([
    purgeCachedImage(getDisplayImageSrc(item)),
    purgeCachedImage(getDownloadImageSrc(item)),
    purgeCachedImage(item?.cachedImageUrl),
    purgeCachedImage(getThumbnailImageSrc(item)),
    ...imageItems.flatMap((image) => [
      purgeCachedImage(image.displayUrl || image.imageUrl),
      purgeCachedImage(image.cachedImageUrl),
      purgeCachedImage(image.thumbnailUrl),
    ]),
  ]);
}

function normalizeImageAssetItem(source) {
  if (typeof source === "string") {
    return {
      imageUrl: source,
      image_url: source,
      displayUrl: source,
      display_url: source,
      cachedImageUrl: "",
      cached_image_url: "",
      thumbnailUrl: thumbnailUrlForDisplay(source),
      thumbnail_url: thumbnailUrlForDisplay(source),
    };
  }
  if (!source || typeof source !== "object") {
    return null;
  }
  const displayUrl = source.displayUrl || source.display_url || source.url || source.imageUrl || source.image_url || source.cachedImageUrl || source.cached_image_url || "";
  if (!displayUrl) {
    return null;
  }
  const cachedImageUrl = source.cachedImageUrl || source.cached_image_url || "";
  const thumbnailUrl = source.thumbnailUrl || source.thumbnail_url || thumbnailUrlForDisplay(cachedImageUrl || displayUrl);
  return {
    ...source,
    imageUrl: source.imageUrl || source.image_url || source.url || displayUrl,
    image_url: source.image_url || source.imageUrl || source.url || displayUrl,
    displayUrl,
    display_url: source.display_url || source.displayUrl || displayUrl,
    cachedImageUrl,
    cached_image_url: source.cached_image_url || source.cachedImageUrl || "",
    thumbnailUrl,
    thumbnail_url: source.thumbnail_url || source.thumbnailUrl || thumbnailUrl,
  };
}

function sanitizeFilePart(value) {
  return String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "image";
}

function formatFileTimestamp(value) {
  return toIsoZ(value || Date.now()).replace(/[-:]/g, "");
}

function detectImageExtension(src) {
  const text = String(src || "");
  const dataUrlMatch = text.match(/^data:image\/([a-zA-Z0-9.+-]+);/);
  if (dataUrlMatch) {
    return normalizeImageExtension(dataUrlMatch[1]);
  }
  try {
    const pathname = new URL(text, window.location.href).pathname;
    const extension = pathname.split(".").pop();
    return normalizeImageExtension(extension);
  } catch {
    return "png";
  }
}

function normalizeImageExtension(extension) {
  const normalized = String(extension || "").toLowerCase();
  if (normalized === "jpeg" || normalized === "jpg") {
    return "jpg";
  }
  if (["png", "webp", "gif", "avif"].includes(normalized)) {
    return normalized;
  }
  return "png";
}

function selectProgressiveInitialSrc(src, placeholderSrc = "") {
  const highResMemoryUrl = getMemoryImageObjectUrl(src);
  if (highResMemoryUrl) {
    return highResMemoryUrl;
  }
  const placeholderMemoryUrl = getMemoryImageObjectUrl(placeholderSrc);
  if (placeholderMemoryUrl) {
    return placeholderMemoryUrl;
  }
  return placeholderSrc || src || "";
}

function getMemoryImageObjectUrl(src) {
  const key = imageCacheKey(src);
  return key ? IMAGE_OBJECT_URL_CACHE.get(key) || "" : "";
}

function preloadImage(src) {
  if (!src || typeof Image === "undefined") {
    return Promise.resolve("");
  }
  const key = imageCacheKey(src);
  if (!key) {
    return Promise.resolve("");
  }
  const pending = IMAGE_LOAD_PROMISES.get(key);
  if (pending) {
    return pending;
  }
  const promise = new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(src);
    image.onerror = () => resolve("");
    image.src = src;
  }).finally(() => {
    IMAGE_LOAD_PROMISES.delete(key);
  });
  IMAGE_LOAD_PROMISES.set(key, promise);
  return promise;
}

async function getCachedImageObjectUrl(src) {
  const key = imageCacheKey(src);
  if (!key) {
    return "";
  }
  const existing = IMAGE_OBJECT_URL_CACHE.get(key);
  if (existing) {
    return existing;
  }
  const pending = IMAGE_OBJECT_URL_PROMISES.get(key);
  if (pending) {
    return pending;
  }
  const promise = getCachedImageBlob(src)
    .then((blob) => {
      if (!blob) {
        return "";
      }
      const current = IMAGE_OBJECT_URL_CACHE.get(key);
      if (current) {
        return current;
      }
      const objectUrl = URL.createObjectURL(blob);
      IMAGE_OBJECT_URL_CACHE.set(key, objectUrl);
      return objectUrl;
    })
    .finally(() => {
      IMAGE_OBJECT_URL_PROMISES.delete(key);
    });
  IMAGE_OBJECT_URL_PROMISES.set(key, promise);
  return promise;
}

async function getCachedImageBlob(src) {
  if (!("caches" in window) || typeof fetch !== "function") {
    return null;
  }
  const url = cacheableImageUrl(src);
  if (!url) {
    return null;
  }
  const cache = await caches.open(IMAGE_CACHE_NAME);
  const request = new Request(url.href, { credentials: "same-origin" });
  const cached = await cache.match(request);
  if (cached?.ok) {
    return cached.blob();
  }
  const response = await fetch(request);
  if (!response.ok) {
    return null;
  }
  await cache.put(request, response.clone());
  return response.blob();
}

async function purgeCachedImage(src) {
  purgeMemoryImage(src);
  if (!("caches" in window)) {
    return;
  }
  const url = cacheableImageUrl(src);
  if (!url) {
    return;
  }
  const cache = await caches.open(IMAGE_CACHE_NAME);
  await cache.delete(new Request(url.href, { credentials: "same-origin" }));
}

function purgeMemoryImage(src) {
  const key = imageCacheKey(src);
  const objectUrl = key ? IMAGE_OBJECT_URL_CACHE.get(key) : "";
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    IMAGE_OBJECT_URL_CACHE.delete(key);
  }
  if (key) {
    IMAGE_OBJECT_URL_PROMISES.delete(key);
  }
}

function sameOriginUrl(src) {
  try {
    const url = new URL(src || "", window.location.href);
    return url.origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
}

function cacheableImageUrl(src) {
  const sameOrigin = sameOriginUrl(src);
  if (sameOrigin) {
    return sameOrigin;
  }
  const assetPath = mainSystemAssetPath(src);
  return assetPath ? new URL(assetPath, window.location.href) : null;
}

function imageCacheKey(src) {
  const cacheableUrl = cacheableImageUrl(src);
  if (cacheableUrl) {
    return cacheableUrl.href;
  }
  try {
    return new URL(src || "", window.location.href).href;
  } catch {
    return String(src || "");
  }
}

function mainSystemAssetPath(src) {
  try {
    const url = new URL(src || "", window.location.href);
    if (url.pathname.startsWith("/generated/")) {
      return `${url.pathname}${url.search}`;
    }
  } catch {
    return "";
  }
  return "";
}

function isExternalGeneratedImageUrl(src) {
  try {
    return new URL(src || "", window.location.href).pathname.startsWith("/v1/images/generated/");
  } catch {
    return false;
  }
}

function downloadableSameOriginUrl(src) {
  const sameOrigin = sameOriginUrl(src);
  if (sameOrigin) {
    return sameOrigin;
  }
  const assetPath = mainSystemAssetPath(src);
  return assetPath ? new URL(assetPath, window.location.href) : null;
}
